// ═══════════════════════════════════════════════════════════
// AI Layer — Anthropic Claude integration
//
// All calls route through /api/claude which proxies to the
// Anthropic API. In dev, vite.config.ts injects the API key.
// In prod, the CF Pages Function (functions/api/claude) does it.
//
// Model: claude-sonnet-4-6
// ═══════════════════════════════════════════════════════════

const CLAUDE_BASE = '/api/claude';
const MODEL = 'claude-sonnet-4-6';

type ContentBlock =
  | { type: 'text'; text: string }
  | { type: 'image'; source: { type: 'base64'; media_type: 'image/jpeg' | 'image/png'; data: string } };

interface Message {
  role: 'user' | 'assistant';
  content: string | ContentBlock[];
}

async function callClaude(
  messages: Message[],
  system?: string,
  maxTokens = 1024,
): Promise<string> {
  const res = await fetch(`${CLAUDE_BASE}/v1/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      model: MODEL,
      max_tokens: maxTokens,
      system,
      messages,
    }),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`AI request failed: ${res.status} ${err}`);
  }
  const data = await res.json() as { content: { type: string; text: string }[] };
  return data.content.find(c => c.type === 'text')?.text ?? '';
}

// ── Classification ────────────────────────────────────────

export interface AIClassificationResult {
  suggestedDepartment: string;
  complexity: 'simple' | 'moderate' | 'complex' | 'very_complex';
  estimatedDays: number;
  flags: string[];
  exemptionRisk: string[];
  duplicateRisk: boolean;
  summary: string;
}

export async function classifyRequest(input: {
  subject: string;
  description: string;
  channel: string;
  jurisdictionLaw?: string;
  departments?: string[];
}): Promise<AIClassificationResult> {
  const system = `You are an expert FOIA (Freedom of Information Act) coordinator assistant.
Analyze incoming public records requests and provide structured classification.
Always respond with valid JSON only. No markdown, no prose.`;

  const deptList = input.departments?.length
    ? `\nAvailable departments: ${input.departments.join(', ')}`
    : '';

  const prompt = `Classify this FOIA request:

Subject: ${input.subject}
Description: ${input.description}
Channel: ${input.channel}
Law: ${input.jurisdictionLaw ?? 'the applicable public records law'}${deptList}

Respond with JSON only:
{
  "suggestedDepartment": "department name or empty string",
  "complexity": "simple|moderate|complex|very_complex",
  "estimatedDays": <number 1-20>,
  "flags": ["overbroad", "voluminous", "sensitive_personnel", "ongoing_litigation", "law_enforcement"],
  "exemptionRisk": ["7(1)(a)", "7(1)(c)"],
  "duplicateRisk": false,
  "summary": "One sentence describing what responsive records would look like"
}`;

  try {
    const text = await callClaude([{ role: 'user', content: prompt }], system, 512);
    const clean = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    return JSON.parse(clean) as AIClassificationResult;
  } catch {
    return {
      suggestedDepartment: '',
      complexity: 'moderate',
      estimatedDays: 10,
      flags: [],
      exemptionRisk: [],
      duplicateRisk: false,
      summary: 'Classification unavailable.',
    };
  }
}

// ── Correspondence Drafting ───────────────────────────────

export interface DraftCorrespondenceInput {
  type: 'acknowledgment' | 'extension' | 'clarification' | 'denial' | 'release' | 'fee_estimate';
  requestNumber: string;
  requesterName: string;
  subject: string;
  jurisdiction: string;
  law?: string;
  deadline?: string;
  exemptions?: string[];
  denialReason?: string;
  feeAmount?: number;
  extensionDays?: number;
  clarificationNeeded?: string;
}

export async function draftCorrespondence(input: DraftCorrespondenceInput): Promise<{ subject: string; body: string }> {
  const system = `You are an experienced government FOIA officer drafting official correspondence.
Write formal, compliant letters. Include statutory citations where applicable.
Respond with JSON: {"subject": "...", "body": "..."}. No markdown outside the JSON.`;

  const typeInstructions: Record<string, string> = {
    acknowledgment: `Acknowledge receipt of the request. Mention the statutory response deadline.`,
    extension:      `Inform the requester of an extension of ${input.extensionDays ?? 14} days. Explain the reason and new deadline (${input.deadline}).`,
    clarification:  `Request clarification needed: ${input.clarificationNeeded ?? '[clarification needed]'}. State the clock is tolled.`,
    denial:         `Deny the request citing: ${(input.exemptions ?? []).join(', ')}. ${input.denialReason ?? ''} Include appeal rights.`,
    release:        `Inform the requester their records are ready. Explain download instructions and any redactions.`,
    fee_estimate:   `Provide a fee estimate of $${input.feeAmount?.toFixed(2) ?? '0.00'} with breakdown. State clock is tolled pending response.`,
  };

  const prompt = `Draft ${input.type} correspondence for:
Request: ${input.requestNumber}
Requester: ${input.requesterName}
Subject: ${input.subject}
Jurisdiction: ${input.jurisdiction}
Law: ${input.law ?? 'the applicable public records law'}
Deadline: ${input.deadline ?? 'N/A'}

Task: ${typeInstructions[input.type] ?? 'Write appropriate official correspondence.'}

Return JSON: {"subject": "Re: ...", "body": "Dear ${input.requesterName},\n\n..."}`;

  try {
    const text = await callClaude([{ role: 'user', content: prompt }], system, 1024);
    const clean = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    return JSON.parse(clean) as { subject: string; body: string };
  } catch {
    return {
      subject: `Re: Your Public Records Request ${input.requestNumber}`,
      body: `Dear ${input.requesterName},\n\n[AI drafting failed — please write your response manually.]\n\nSincerely,\n${input.jurisdiction} FOIA Officer`,
    };
  }
}

// ── Redaction Suggestion ──────────────────────────────────

export interface RedactionSuggestion {
  text:          string;
  reason:        string;
  exemptionCode: string;
  confidence:    number; // 0–1
}

export async function suggestRedactions(input: {
  documentText: string;
  exemptionCodes: { code: string; title: string }[];
  jurisdiction?: string;
  customInstruction?: string;
  /** Base64-encoded JPEG of the rendered page. When provided, Claude analyses
   *  the image directly so it catches form field values, non-standard encoded
   *  text, and anything else that PDF text extraction misses. */
  pageImageBase64?: string;
}): Promise<RedactionSuggestion[]> {
  const exList = input.exemptionCodes.map(e => `${e.code}: ${e.title}`).join('\n');
  const text   = input.documentText.slice(0, 6000); // limit context

  // Two modes:
  //   Targeted — user gave a specific instruction ("all numbers", "names", "Project X")
  //              → literal search, return every match, skip exemption analysis
  //   General  — no instruction → FOIA-aware analysis with confidence threshold
  let system: string;
  let prompt: string;

  if (input.customInstruction) {
    system = `You are an expert FOIA/public records redaction analyst helping staff reviewers find text to redact from government documents.

Staff often use shorthand when describing redaction targets. Interpret these intelligently:

COMMON SHORTHAND → WHAT TO FIND:
- "names" / "people" / "individuals" → proper names of people (first, last, full names, initials like "J. Smith")
- "SSN" / "social security" / "social" → Social Security Numbers in any format: 123-45-6789, 123456789, XXX-XX-XXXX
- "addresses" / "home address" → street addresses: house numbers, street names, apt/unit numbers, city/state/zip
- "phone" / "phone numbers" / "numbers" → telephone numbers in any format: (555) 123-4567, 555-123-4567, 5551234567
- "DOB" / "date of birth" / "birthday" / "born" → birth dates in any format: MM/DD/YYYY, Month DD YYYY, age references
- "account" / "account numbers" → financial account, case, ID, or reference numbers
- "email" / "email addresses" → email addresses in any format
- "license" / "DL" → driver's license numbers, professional license numbers
- "medical" / "diagnosis" / "health" → medical conditions, diagnoses, medications, treatment details
- "financial" / "income" / "salary" / "wages" → dollar amounts, income, salary, financial figures
- "EIN" / "tax ID" → Employer Identification Numbers or Tax ID Numbers
- "passport" → passport numbers
- "signature" → signature blocks, signed-by fields

For specific terms (e.g., "Project Falcon", "John Smith", a specific case number), find exact or close matches.

Be exhaustive — return every match. Do not second-guess whether something is sensitive enough. Staff have already decided what category needs redacting. Respond with JSON array only, no prose, no markdown fences.`;

    const bestCode = input.exemptionCodes[0]?.code ?? 'PII';

    prompt = `Find every instance of: "${input.customInstruction}"

DOCUMENT:
${text}

AVAILABLE EXEMPTION CODES (use the most appropriate one per match):
${exList || bestCode}

Instructions:
- Interpret the search term using your knowledge of common FOIA redaction targets
- Return the exact text as it appears in the document (copy it verbatim)
- Cover every occurrence — do not stop after the first match
- Choose the most applicable exemption code for each match
- Do NOT omit matches because you think they are not sensitive enough

JSON array:
[
  {
    "text": "exact verbatim text from document",
    "reason": "matches: ${input.customInstruction}",
    "exemptionCode": "code",
    "confidence": 1.0
  }
]

Return [] only if the document genuinely contains nothing matching the description.`;
  } else {
    system = `You are an expert FOIA redaction analyst. Identify text in documents that should be redacted under the provided exemption codes. Respond with JSON array only.`;

    prompt = `Review this document text and suggest redactions:

DOCUMENT:
${text}

EXEMPTION CODES:
${exList || 'Use standard FOIA exemptions (PII, personnel, law enforcement, attorney-client)'}

Identify specific text passages that should be redacted. Return JSON array:
[
  {
    "text": "exact text to redact",
    "reason": "why this should be redacted",
    "exemptionCode": "code",
    "confidence": 0.9
  }
]

Only include high-confidence suggestions (>0.7). Return empty array [] if no redactions needed.`;
  }

  function parseResponse(response: string): RedactionSuggestion[] {
    const stripped = response.replace(/```json\n?/g, '').replace(/```\n?/g, '');
    const start = stripped.indexOf('[');
    const end   = stripped.lastIndexOf(']');
    if (start === -1 || end === -1 || end < start) return [];
    const arr = JSON.parse(stripped.slice(start, end + 1));
    return Array.isArray(arr) ? arr as RedactionSuggestion[] : [];
  }

  // When a rendered page image is available, send it alongside the prompt so
  // Claude can read text that PDF extraction misses (form fields, non-standard
  // encodings, lightly scanned content, etc.). If the multimodal call fails
  // for any reason, fall back to text-only so existing PDFs keep working.
  if (input.pageImageBase64) {
    const multimodalContent: Message['content'] = [
      { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: input.pageImageBase64 } },
      { type: 'text', text: prompt },
    ];
    try {
      return parseResponse(await callClaude([{ role: 'user', content: multimodalContent }], system, 4096));
    } catch {
      // fall through to text-only below
    }
  }

  try {
    return parseResponse(await callClaude([{ role: 'user', content: prompt }], system, 4096));
  } catch {
    return [];
  }
}

// ── Denial Letter Drafting ────────────────────────────────

export async function draftDenialLetter(input: {
  requestNumber: string;
  requesterName: string;
  subject: string;
  exemptionCodes: { code: string; title: string; citation: string }[];
  withheldDescription: string;
  jurisdiction: string;
}): Promise<string> {
  const system = `You are a government FOIA officer drafting denial letters.
Write formal, legally compliant denial letters with proper exemption citations and appeal rights.
Return the letter body text only (no JSON, no subject line).`;

  const codeList = input.exemptionCodes.map(e => `${e.code} (${e.citation}): ${e.title}`).join('\n');

  const prompt = `Draft a FOIA denial letter:

Request: ${input.requestNumber}
Requester: ${input.requesterName}
Subject: ${input.subject}
Jurisdiction: ${input.jurisdiction}
Withheld: ${input.withheldDescription}

Applicable Exemptions:
${codeList}

Include: citation of applicable exemptions, description of withheld records, appeal rights notice (60 days to Public Access Counselor).`;

  try {
    return await callClaude([{ role: 'user', content: prompt }], system, 1200);
  } catch {
    return `Dear ${input.requesterName},\n\n[AI drafting failed — please write the denial letter manually.]\n\nSincerely,\n${input.jurisdiction} FOIA Officer`;
  }
}

// ── Analytics Summary ─────────────────────────────────────

export async function generateAnalyticsSummary(stats: {
  totalRequests: number;
  overdueCount: number;
  averageDaysToClose: number;
  topDepartments: string[];
  exemptionBreakdown: Record<string, number>;
}): Promise<string> {
  const system = `You are a government transparency analyst. Write concise, actionable insights about FOIA compliance trends. 2-3 sentences max.`;

  const prompt = `Analyze these FOIA statistics:
- Total active requests: ${stats.totalRequests}
- Overdue: ${stats.overdueCount}
- Avg days to close: ${stats.averageDaysToClose}
- Top departments by volume: ${stats.topDepartments.join(', ')}
- Top exemptions used: ${Object.entries(stats.exemptionBreakdown).slice(0, 5).map(([k, v]) => `${k}(${v})`).join(', ')}

Provide 2-3 sentences of key insights and recommended actions.`;

  try {
    return await callClaude([{ role: 'user', content: prompt }], system, 256);
  } catch {
    return 'Analytics summary unavailable.';
  }
}
